import { useEffect, useRef, useState } from 'react';
import {
  AppDispatch,
  bookingsWithImages as setBookingsWithImages,
  fetchContentFragmentsFromCms,
  fetchPortImagesFromFragments,
  getActiveSession,
  GetAllBookingsMyMSC,
  GetItineraryMyMSC,
  mergeItinerariesByCruiseId,
  myAreaAllBookings,
  RootState,
} from '@msc-b2c/shared-utils';
import {
  AllBooking,
  GenericConfigType,
  IFetchShipsFromFragmentsResponse,
  IFetchShipsFromFragmentsURLParams,
  PortItem,
  ShipData,
} from '@msc-b2c/interfaces/interfaces';
import { ItineraryResponse } from '@msc-b2c/interfaces/types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  computeBookingsImagesFromMetadata,
  needsPortImagesForItinerary,
  upgradeBookingsImagesWithItineraryCodes,
} from '../../imageRotation/imageRotation';

export function useGetAllCruises(genericConfigs: GenericConfigType) {
  const [data, setData] = useState<object>();
  const dispatch = useDispatch<AppDispatch>();
  // useStore lets us read the latest bookingsWithImages snapshot inside the
  // async effect without subscribing to it (a useSelector dependency would
  // cause a refetch loop because this hook also dispatches updates to that
  // same slice).
  const store = useStore<RootState>();
  // Prevents re-fetching when genericConfigs gets a new object reference
  // (e.g. because search-bar and my-msc both dispatch setConfigs on page
  // load, each producing a new Redux state object for the same config data).
  const hasFetchedRef = useRef(false);

  // Subscribe to the refetch token: when link-booking increments it,
  // invalidateAllBookings() also sets allBookings→null so the next fetch
  // effect run sees no cached data and calls the API.
  const bookingsRefetchToken = useSelector(
    (state: RootState) => state.myCruise.bookingsRefetchToken ?? 0
  );

  // Reset the fetch guard whenever a forced-refetch is requested so the
  // effect below runs again even though the component is still mounted.
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [bookingsRefetchToken]);

  useEffect(() => {
    // Wait until the countryCodeISO3 filter value is available — the effect
    // may run once with the initial (empty) genericConfigs before setConfigs
    // has been dispatched, which would filter out all bookings incorrectly.
    const countryCodeISO3 = genericConfigs?.configs?.b2cCommerceConfig?.countryCodeISO3;
    if (!countryCodeISO3) return;

    // Guard against duplicate fetches caused by genericConfigs changing
    // reference (multiple setConfigs dispatches from different micro-apps).
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const { email } = getActiveSession();

    async function fetchData() {
      if (!email) return;
      try {
        // Reuse cached allBookings from Redux when available (e.g. component
        // remounted after a booking switch — resetMyCruiseData preserves the
        // list so the /users/{email}/bookings API call is not needed again).
        const cachedBookings = store.getState()?.myCruise?.allBookings;
        let filteredBookings: AllBooking[];

        if (cachedBookings !== null && cachedBookings !== undefined) {
          filteredBookings = cachedBookings;
        } else {
          const getBooks = new GetAllBookingsMyMSC(false, { email: email });
          const getBooksData = await getBooks.getData();

          // Filter only the cruises that have the same marketCode as the website.
          filteredBookings = getBooksData?.bookings?.filter(
            (booking) => booking.marketCode === countryCodeISO3
          );
          getBooksData.bookings = filteredBookings;

          // Dispatch immediately so sibling components on the same page
          // (VoyagerClubTeaserWrapper, AddExistingBookingWrapper) can reuse
          // this data from Redux without issuing their own /bookings calls
          // while the slower getShips + per-booking /itinerary work completes.
          dispatch(myAreaAllBookings(filteredBookings));
        }

        if (filteredBookings.length === 0) {
          document.querySelector('.cmp-allmycruises-skeleton')?.classList.add('hide');
          return;
        }

        const currentBookingsWithImages =
          store.getState()?.myAreaCommonConfig?.bookingsWithImages ?? [];

        // Phase 1 fallback: if the login flow did not seed the rotation
        // (e.g. the user is on a light-login session, or sessionStorage was
        // cleared), compute it now from the freshly fetched bookings — pure,
        // no API calls.
        const phase1Images =
          currentBookingsWithImages.length === 0
            ? computeBookingsImagesFromMetadata(filteredBookings, genericConfigs)
            : currentBookingsWithImages;

        if (currentBookingsWithImages.length === 0 && phase1Images.length > 0) {
          dispatch(setBookingsWithImages(phase1Images));
        }

        let shipsData: ShipData[] = [];
        try {
          const result = await fetchContentFragmentsFromCms<
            IFetchShipsFromFragmentsResponse,
            IFetchShipsFromFragmentsURLParams
          >('getShips');
          shipsData = result?.data?.shipCfmList?.items;
        } catch {
          shipsData = [];
        }

        // Per-booking itinerary fetch is required here to render the All My
        // Cruises cards (departure dates, ports, etc.). This is the ONLY page
        // where the per-booking /itinerary fan-out is allowed.
        const itineraryByCruiseId: Record<string, ItineraryResponse | undefined> = {};
        const itineraries = await Promise.all(
          filteredBookings.map(async (booking) => {
            const getItin = new GetItineraryMyMSC(false, { cruiseId: booking.cruiseId });
            try {
              const itinData = await getItin.getData();
              itineraryByCruiseId[booking.cruiseId] = itinData;
              return itinData;
            } catch {
              itineraryByCruiseId[booking.cruiseId] = undefined;
              return undefined;
            }
          })
        );

        // Persist successful itineraries in the per-cruiseId cache so the
        // Overview can reuse them after the user selects a card here (post
        // token-upgrade) instead of re-fetching the same /itinerary endpoint.
        dispatch(mergeItinerariesByCruiseId(itineraryByCruiseId));

        // Phase 2 upgrade: apply the full image priority chain now that we
        // have itinerary data (itinCode → port images → area → fallback).
        // Conditionally fetch port images from CMS only when at least one
        // booking doesn't have a static itineraryCodesImagesConfig entry —
        // the same "needsPortImages" pattern as in assignImageToBooking.
        const needsPortImages = Object.values(itineraryByCruiseId).some((itin) =>
          needsPortImagesForItinerary(itin, genericConfigs)
        );

        let portsData: PortItem[] | null = null;
        if (needsPortImages) {
          portsData = await fetchPortImagesFromFragments(dispatch);
        }

        const upgradedImages = upgradeBookingsImagesWithItineraryCodes(
          phase1Images,
          filteredBookings,
          itineraryByCruiseId,
          genericConfigs,
          portsData
        );
        if (upgradedImages !== phase1Images) {
          dispatch(setBookingsWithImages(upgradedImages));
        }

        const cardsData = filteredBookings.map((booking, index) => {
          const shipCode = booking.cruiseId.slice(0, 2);
          const shipData = shipsData?.find((item: ShipData) => item.shipCode === shipCode);
          const imageData = upgradedImages.find((b) => b.bookingNumber === booking.number)?.image;

          return {
            bookingData: booking,
            itinerary: itineraries[index],
            shipData: shipData,
            imageData: imageData,
          };
        });

        setData(cardsData);
      } catch (err) {
        console.log(err);
      }
    }

    fetchData();
  }, [dispatch, genericConfigs, store, bookingsRefetchToken]);

  return data;
}
